import type { ApiResponse } from "@/types/common";
import type {
  GetOnboardingQuestionsResponse,
  GetQuestionParams,
  GetQuestionResponse,
  OnboardingState,
  PostOnboardingAnswerRequest,
  PostOnboardingAnswerResponse,
  PostQuestionRequest,
  PostQuestionResponse,
  QuestionField,
} from "@/types/question";
import { privateRequest } from "../axios";

const ENDPOINTS = {
  QUESTIONS: "/questions",
  ONBOARDING_QUESTIONS: "/questions/onboarding",
};

const getAllQuestions = async (params?: GetQuestionParams): Promise<GetQuestionResponse> => {
  const response = await privateRequest.get<ApiResponse<GetQuestionResponse>>(
    ENDPOINTS.QUESTIONS,
    { params },
  );
  return {
    results: response?.data?.data?.results ?? [],
    totalRecord: response?.data?.data?.totalRecord ?? 0,
  };
};

const getOnboardingQuestions = async (): Promise<OnboardingState> => {
  const response = await privateRequest.get<ApiResponse<OnboardingState>>(
    ENDPOINTS.ONBOARDING_QUESTIONS,
  );
  const data = response?.data?.data;
  return {
    status: data?.status ?? "NOT_STARTED",
    totalQuestions: data?.totalQuestions ?? 0,
    answeredCount: data?.answeredCount ?? 0,
    remainingCount: data?.remainingCount ?? 0,
    question: data?.question ?? null,
  };
};

const postQuestion = async (data: PostQuestionRequest): Promise<PostQuestionResponse> => {
  const response = await privateRequest.post<ApiResponse<PostQuestionResponse>>(
    ENDPOINTS.QUESTIONS,
    data,
  );
  return response?.data?.data ?? {};
};

const updateQuestion = async (id: string, data: PostQuestionRequest): Promise<PostQuestionResponse> => {
  const response = await privateRequest.patch<ApiResponse<PostQuestionResponse>>(
    `${ENDPOINTS.QUESTIONS}/${id}`,
    data,
  );
  return response?.data?.data ?? {};
};

const updateQuestionPartial = async (id: string | number, data: Partial<PostQuestionRequest>): Promise<any> => {
  const response = await privateRequest.patch<ApiResponse<any>>(
    `${ENDPOINTS.QUESTIONS}/${id}`,
    data,
  );
  return response?.data?.data ?? {};
};

const deleteQuestion = async (id: string): Promise<void> => {
  await privateRequest.delete(`${ENDPOINTS.QUESTIONS}/${id}`);
}

const exportQuestions = async ({
  userType,
} : {
  userType: "INVESTOR" | "FOUNDER";
}) => {
  return privateRequest.get(ENDPOINTS.QUESTIONS, {
    params: {
        userType,
        isExport: true,
    },
    responseType: "blob",
  })
}

const submitOnboardingAnswer = async (
  questionId: string | number,
  data: PostOnboardingAnswerRequest,
): Promise<PostOnboardingAnswerResponse> => {
  const response = await privateRequest.patch<ApiResponse<PostOnboardingAnswerResponse>>(
    `${ENDPOINTS.QUESTIONS}/${questionId}/answer`,
    data,
  );

  return response?.data?.data ?? {};
};

const QuestionnaireService = {
  getAllQuestions,
  getOnboardingQuestions,
  postQuestion,
  updateQuestion,
  updateQuestionPartial,
  deleteQuestion,
  exportQuestions,
  submitOnboardingAnswer,
};

export default QuestionnaireService;
